import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

// Vista de Configuración › Catálogos sincronizada con la URL (UX-NAV-02): catálogo
// abierto, filtro de estado y, en normas, la norma elegida. Los valores por defecto
// no se escriben en la URL, y un valor desconocido cae al defecto.

export type CatalogTab = 'tipos' | 'procesos' | 'normas' | 'campos';
export type StatusFilter = 'todos' | 'activos' | 'desactivados';

export const CATALOG_TABS: { value: CatalogTab; label: string }[] = [
  { value: 'tipos', label: 'Tipos de documento' },
  { value: 'procesos', label: 'Procesos' },
  { value: 'normas', label: 'Normas y cláusulas' },
  { value: 'campos', label: 'Campos propios' },
];

export const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'activos', label: 'Activos' },
  { value: 'desactivados', label: 'Desactivados' },
];

export interface CatalogView {
  tab: CatalogTab;
  status: StatusFilter;
  /** Norma filtrada en la pestaña de normas; null = todas. */
  norm: string | null;
}

function pick<T extends string>(raw: string | null, options: { value: T }[], fallback: T): T {
  return options.find((o) => o.value === raw)?.value ?? fallback;
}

export function filterByStatus<T extends { isActive: boolean }>(
  rows: readonly T[],
  status: StatusFilter,
): T[] {
  if (status === 'todos') {
    return [...rows];
  }
  return rows.filter((r) => r.isActive === (status === 'activos'));
}

export interface UseCatalogView {
  view: CatalogView;
  setTab: (tab: CatalogTab) => void;
  setStatus: (status: StatusFilter) => void;
  setNorm: (norm: string | null) => void;
}

export function useCatalogView(): UseCatalogView {
  const [params, setParams] = useSearchParams();

  const view = useMemo<CatalogView>(() => {
    const tab = pick(params.get('catalogo'), CATALOG_TABS, 'tipos');
    const norm = params.get('norma');
    return {
      tab,
      status: pick(params.get('estado'), STATUS_FILTERS, 'todos'),
      norm: tab === 'normas' && norm !== null && norm !== '' ? norm : null,
    };
  }, [params]);

  const update = useCallback(
    (apply: (p: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          apply(p);
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setTab = useCallback(
    (tab: CatalogTab) =>
      update((p) => {
        p.delete('norma');
        if (tab === 'tipos') {
          p.delete('catalogo');
        } else {
          p.set('catalogo', tab);
        }
      }),
    [update],
  );

  const setStatus = useCallback(
    (status: StatusFilter) =>
      update((p) => (status === 'todos' ? p.delete('estado') : p.set('estado', status))),
    [update],
  );

  const setNorm = useCallback(
    (norm: string | null) =>
      update((p) => (norm === null ? p.delete('norma') : p.set('norma', norm))),
    [update],
  );

  return { view, setTab, setStatus, setNorm };
}
